/**
 * OMNITrade Interface Guide Page
 * Public page explaining each part of the trading bot interface
 */

import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Monitor, Wallet, DollarSign, TrendingUp, BarChart3,
  Activity, Play, Pause, Square, Eye, Settings, Bell, Info
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { SyncLogoWithText } from '@/components/Logo';

// Card component
const Card = ({ children, className = '' }) => (
  <div className={`bg-slate-800/50 border border-slate-700/50 rounded-xl p-6 ${className}`}>
    {children}
  </div>
);

// Interface Section component
const InterfaceSection = ({ number, title, description, details, color = "blue" }) => {
  const colorClasses = {
    blue: "bg-blue-600 border-blue-500/30",
    green: "bg-green-600 border-green-500/30",
    purple: "bg-purple-600 border-purple-500/30",
    orange: "bg-orange-600 border-orange-500/30",
    cyan: "bg-cyan-600 border-cyan-500/30",
    rose: "bg-rose-600 border-rose-500/30",
  };

  return (
    <div className={`p-6 bg-slate-700/30 rounded-lg border ${colorClasses[color].split(' ')[1]} mb-6`}>
      <div className="flex items-start gap-4">
        <div className={`w-10 h-10 ${colorClasses[color].split(' ')[0]} rounded-lg flex items-center justify-center flex-shrink-0`}>
          <span className="text-white font-bold">{number}</span>
        </div>
        <div className="flex-1">
          <h3 className="text-white font-semibold text-lg mb-2">{title}</h3>
          <p className="text-slate-300 mb-4">{description}</p>
          {details && (
            <div className="bg-slate-800/50 rounded-lg p-4">
              {details}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const InterfaceGuidePage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0a1628]/80 backdrop-blur-lg border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <SyncLogoWithText variant="dark" />
            <div className="hidden md:flex items-center gap-8">
              <a href="/" className="text-gray-300 hover:text-white transition-colors text-sm">Home</a>
              <a href="/interface-guide" className="text-[#5A9BD5] font-medium text-sm">Interface Guide</a>
              <a href="/strategies" className="text-gray-300 hover:text-white transition-colors text-sm">Strategies</a>
            </div>
            <div className="flex items-center gap-3">
              <Button 
                variant="ghost" 
                className="text-gray-300 hover:text-white hover:bg-white/10"
                onClick={() => navigate('/auth')}
              >
                Log In
              </Button>
              <Button 
                className="bg-[#5A9BD5] hover:bg-[#4A8BC5] text-white"
                onClick={() => navigate('/auth?mode=register')}
              >
                Sign Up Free
              </Button>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-5xl mx-auto px-4 pt-24 pb-12">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 px-4 py-2 bg-slate-800/50 border border-slate-700/50 rounded-lg text-white hover:bg-slate-700/50 transition"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </button>
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <Monitor className="h-6 w-6 text-[#5A9BD5]" />
              Interface Guide
            </h1>
            <p className="text-slate-400 text-sm">Understanding every part of the OMNITrade dashboard</p>
          </div>
        </div>

        {/* Screenshot */}
        <Card className="mb-8">
          <h2 className="text-xl font-bold text-white mb-4">The Trading Dashboard</h2>
          <p className="text-slate-300 mb-6">
            The OMNITrade desktop application provides a comprehensive trading dashboard with real-time data, 
            strategy indicators, and complete control over your automated trading.
          </p>
          <div className="rounded-xl overflow-hidden border border-slate-600/50 shadow-2xl">
            <img 
              src="/assets/app-interface.png" 
              alt="OMNITrade Trading Interface" 
              className="w-full h-auto"
            />
          </div>
        </Card>

        {/* Interface Sections */}
        <Card className="mb-8">
          <h2 className="text-xl font-bold text-white mb-6">Interface Components Explained</h2>

          <InterfaceSection
            number="1"
            title="Header Bar - Status & Account"
            description="The top bar shows your account status, connection info, and quick access controls."
            color="blue"
            details={
              <ul className="text-slate-300 space-y-2 text-sm">
                <li><strong className="text-white">Credits:</strong> Your remaining trading credits (pay-per-trade model)</li>
                <li><strong className="text-white">Connected:</strong> Connection status to the server (green = connected)</li>
                <li><strong className="text-white">Updates:</strong> Number of real-time price updates received</li>
                <li><strong className="text-white">Help:</strong> Quick access to documentation and support</li>
                <li><strong className="text-white">Profile:</strong> Account settings and logout option</li>
              </ul>
            }
          />

          <InterfaceSection
            number="2"
            title="Trading Controls"
            description="Manual trading buttons for when you want to override the bot's decisions."
            color="green"
            details={
              <ul className="text-slate-300 space-y-2 text-sm">
                <li><strong className="text-green-400">Buy:</strong> Manually open a LONG position (profit when price goes up)</li>
                <li><strong className="text-rose-400">Short:</strong> Manually open a SHORT position (profit when price goes down)</li>
                <li><strong className="text-white">Close:</strong> Close your current position at market price</li>
                <li><strong className="text-yellow-400">Pause:</strong> Temporarily pause the bot (keeps position open)</li>
                <li><strong className="text-red-400">Stop:</strong> Stop the bot completely</li>
              </ul>
            }
          />

          <InterfaceSection
            number="3"
            title="Status Badges"
            description="Quick visual indicators showing your current trading configuration."
            color="purple"
            details={
              <ul className="text-slate-300 space-y-2 text-sm">
                <li><strong className="text-white">Exchange Badge:</strong> Shows which exchange you're connected to (e.g., BITFINEX)</li>
                <li><strong className="text-green-400">Live Trading / Paper:</strong> Indicates if you're using real money or simulation</li>
                <li><strong className="text-green-400">WS Live:</strong> WebSocket connection status for real-time data</li>
                <li><strong className="text-green-400">API Keys OK:</strong> Confirms your exchange API keys are valid</li>
                <li><strong className="text-white">Strategy Name:</strong> Currently selected trading strategy</li>
                <li><strong className="text-green-400">Bot is RUNNING:</strong> Shows the bot's current state</li>
              </ul>
            }
          />

          <InterfaceSection
            number="4"
            title="Current Market Condition"
            description="AI-powered analysis of current market conditions to help select the best strategy."
            color="orange"
            details={
              <ul className="text-slate-300 space-y-2 text-sm">
                <li><strong className="text-yellow-400">Low Volatility (Squeeze):</strong> Market is consolidating, breakout expected</li>
                <li><strong className="text-green-400">Active Strategy:</strong> The strategy currently being executed</li>
                <li><strong className="text-cyan-400">Recommended:</strong> Strategy suggested based on market analysis</li>
                <li><strong className="text-slate-400">Click for details:</strong> View full market analysis breakdown</li>
              </ul>
            }
          />

          <InterfaceSection
            number="5"
            title="Portfolio Balance"
            description="Real-time view of your exchange account balance."
            color="cyan"
            details={
              <ul className="text-slate-300 space-y-2 text-sm">
                <li><strong className="text-white">Total Balance:</strong> Combined value of all assets in USD</li>
                <li><strong className="text-white">USD:</strong> Your available USD/stablecoin balance</li>
                <li><strong className="text-white">BTC:</strong> Your Bitcoin holdings (or other trading pair)</li>
              </ul>
            }
          />

          <InterfaceSection
            number="6"
            title="BTC Price Panel"
            description="Real-time price information for your trading pair."
            color="rose"
            details={
              <ul className="text-slate-300 space-y-2 text-sm">
                <li><strong className="text-white">Last Price:</strong> Most recent trade price</li>
                <li><strong className="text-green-400">Bid:</strong> Highest buy order price</li>
                <li><strong className="text-white">Spread:</strong> Difference between bid and ask</li>
                <li><strong className="text-red-400">Ask:</strong> Lowest sell order price</li>
                <li><strong className="text-white">24H Range:</strong> Lowest and highest prices in the last 24 hours</li>
                <li><strong className="text-white">Signal:</strong> Current strategy signal (BUY/SELL/HOLD)</li>
              </ul>
            }
          />

          <InterfaceSection
            number="7"
            title="Current Position"
            description="Details of your open trade, if any."
            color="green"
            details={
              <ul className="text-slate-300 space-y-2 text-sm">
                <li><strong className="text-white">Position Type:</strong> LONG (green) or SHORT (red)</li>
                <li><strong className="text-white">Unrealized P&L:</strong> Current profit/loss if you closed now</li>
                <li><strong className="text-white">Entry Price:</strong> Price at which you entered the position</li>
                <li><strong className="text-white">Amount:</strong> Size of your position in crypto</li>
                <li><strong className="text-white">Min to close:</strong> Minimum profit to close (from settings)</li>
              </ul>
            }
          />

          <InterfaceSection
            number="8"
            title="Financial Performance"
            description="Track your trading performance over time."
            color="blue"
            details={
              <ul className="text-slate-300 space-y-2 text-sm">
                <li><strong className="text-white">Session P&L:</strong> Profit/loss since bot started</li>
                <li><strong className="text-white">Trade Count:</strong> Number of trades with wins/losses</li>
                <li><strong className="text-white">Today (Live):</strong> Today's live trading performance</li>
                <li><strong className="text-white">Gross/Net:</strong> Toggle between gross P&L and net (after credits)</li>
                <li><strong className="text-slate-400">Click for detailed stats:</strong> View full performance breakdown</li>
              </ul>
            }
          />

          <InterfaceSection
            number="9"
            title="Price Chart"
            description="Visual representation of price movements with multiple timeframes."
            color="purple"
            details={
              <ul className="text-slate-300 space-y-2 text-sm">
                <li><strong className="text-white">Timeframes:</strong> 1m, 5m, 15m, 1H, 4H, 1D - select your preferred view</li>
                <li><strong className="text-white">Chart Type:</strong> Toggle between line chart and candlestick view</li>
                <li><strong className="text-green-400">Live indicator:</strong> Shows real-time updates are active</li>
                <li><strong className="text-white">Candlesticks:</strong> Green = price went up, Red = price went down</li>
              </ul>
            }
          />

          <InterfaceSection
            number="10"
            title="Strategy Indicator Panel"
            description="Real-time display of your selected strategy's signals and values."
            color="orange"
            details={
              <ul className="text-slate-300 space-y-2 text-sm">
                <li><strong className="text-white">RSI Value:</strong> Current RSI reading (0-100 scale)</li>
                <li><strong className="text-green-400">Oversold Zone (below 30):</strong> Potential buy opportunity</li>
                <li><strong className="text-red-400">Overbought Zone (above 70):</strong> Potential sell opportunity</li>
                <li><strong className="text-blue-400">Neutral:</strong> No clear signal, bot waits</li>
                <li><strong className="text-white">Status Badge:</strong> Current signal interpretation</li>
              </ul>
            }
          />

          <InterfaceSection
            number="11"
            title="Technical Indicators (Bottom Row)"
            description="Additional technical indicators for comprehensive market analysis."
            color="cyan"
            details={
              <ul className="text-slate-300 space-y-2 text-sm">
                <li><strong className="text-white">RSI Chart:</strong> Historical RSI values with overbought/oversold zones</li>
                <li><strong className="text-white">EMA Crossover:</strong> Fast vs Slow EMA for trend direction (Bullish/Bearish)</li>
                <li><strong className="text-white">ADX:</strong> Trend strength indicator (0-100, above 25 = strong trend)</li>
              </ul>
            }
          />

          <InterfaceSection
            number="12"
            title="Recent Trades"
            description="A scrollable history of your recent trades showing key details and outcomes."
            color="purple"
            details={
              <ul className="text-slate-300 space-y-2 text-sm">
                <li><strong className="text-white">Trade Type:</strong> Shows whether each trade was a LONG (green) or SHORT (red) position</li>
                <li><strong className="text-white">Entry/Exit Prices:</strong> The prices at which the position was opened and closed</li>
                <li><strong className="text-white">Amount:</strong> Size of the trade in cryptocurrency units</li>
                <li><strong className="text-white">P&L:</strong> Profit or loss from each trade (green for profit, red for loss)</li>
                <li><strong className="text-white">Timestamp:</strong> Date and time when the trade was executed</li>
                <li><strong className="text-white">Strategy:</strong> Which trading strategy was used for the trade</li>
                <li><strong className="text-white">Reason:</strong> Why the trade was closed (e.g., take profit, stop loss, signal change)</li>
                <li><strong className="text-slate-400">Scroll:</strong> Scroll down to view older trades in your history</li>
              </ul>
            }
          />

          <InterfaceSection
            number="13"
            title="Footer Status Bar"
            description="Bottom bar showing app version and bot status."
            color="rose"
            details={
              <ul className="text-slate-300 space-y-2 text-sm">
                <li><strong className="text-white">App Version:</strong> Current OMNITrade desktop app version</li>
                <li><strong className="text-white">Strategy:</strong> Currently active trading strategy</li>
                <li><strong className="text-green-400">Bot Active:</strong> Green indicator when bot is running</li>
              </ul>
            }
          />
        </Card>

        {/* CTA */}
        <div className="text-center mt-12">
          <h2 className="text-2xl font-bold text-white mb-4">Ready to Start Trading?</h2>
          <p className="text-gray-400 mb-6">Sign up for free and download the desktop app to get started.</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button 
              size="lg"
              className="bg-[#5A9BD5] hover:bg-[#4A8BC5] text-white px-8"
              onClick={() => navigate('/auth?mode=register')}
            >
              Sign Up Free
            </Button>
            <Button 
              size="lg"
              variant="outline"
              className="border-gray-600 text-gray-300 hover:bg-white/5 px-8"
              onClick={() => navigate('/strategies')}
            >
              Learn About Strategies
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InterfaceGuidePage;
